import { SegmentStatus } from 'business/receipt/constants';
import { db } from 'technical/db';

export type BackendRing = {
  id: string;
  number: number;
  createdAt: string;
};

export const ringSyncService = {
  async download(backendRings: BackendRing[]): Promise<void> {
    const records = backendRings.map((r) => ({
      id: r.id,
      number: r.number,
      createdAt: r.createdAt,
    }));

    await db.rings.bulkPut(records);

    // A ring can be scanned ahead of its creation, using a virtual placeholder
    // slot ("virtualRingN") as `ringId`. `virtualRingNumber` is the only
    // reliable marker of that pending state: `ringId` itself can already have
    // been reset to null/undefined by a regular segment download by the time
    // the real ring is created (the backend never learns the virtual id, so
    // it legitimately reports no ringId for that segment in the meantime).
    // Once the ring actually gets created and downloaded, its segments must
    // be reassigned to the ring's stable id, otherwise they stay orphaned
    // forever and disappear from the now-real ring's segment list/count.
    for (const ring of records) {
      const orphanedSegments = await db.segments
        .where('virtualRingNumber')
        .equals(ring.number)
        .toArray();

      if (orphanedSegments.length > 0) {
        await db.segments.bulkUpdate(
          orphanedSegments.map((segment) => ({
            key: segment.serialNumber,
            changes: {
              ringId: ring.id,
              virtualRingNumber: undefined,
              // The segment was scanned with a VIRTUAL status (no real ring existed yet).
              // Now that the ring is real, it must become INSTALLED, otherwise a pending
              // (not-yet-uploaded) segment ends up sent to the backend with the invalid
              // combination `status: VIRTUAL` + a real `ringId` (instead of
              // `virtualRingNumber`), which matches none of the backend's upload branches
              // and is silently dropped — desyncing the mobile app's optimistic "assigned"
              // state from the backend's actual (unassigned) state.
              ...(segment.status === SegmentStatus.Virtual && {
                status: SegmentStatus.Installed,
              }),
            },
          })),
        );
      }
    }
  },
};
